import React, { useEffect, useId, useRef, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalTitle } from 'design-system/src/components/Modal';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { RadioButton, RadioGroup, RadioLabel, RadioOption } from 'design-system/src/components/Radio/Radio';
import { Input } from 'design-system/src/components/Input/Input';
import Button from 'design-system/src/components/Button/Button';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import { toast } from 'react-hot-toast';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { useRemoveProvider } from '../../../hooks/useProviders';

export type RemoveProviderModalProps = {
  open: boolean;
  onClose: () => void;
  provider: { provider_id: string; npi: string; first_name: string; last_name: string };
};

// NOTE: keep in sync with ProviderChurnReason in python/models/practice.py
type ChurnReason = 'retired' | 'left_practice' | 'not_interested' | 'unknown';

type ReasonOption = {
  label: string;
  value: ChurnReason;
};

const reasonOptions: ReasonOption[] = [
  { label: 'Provider has retired', value: 'retired' },
  { label: 'Provider left the practice', value: 'left_practice' },
  { label: 'Provider is no longer interested in using Tandem', value: 'not_interested' },
  { label: 'Other', value: 'unknown' },
];

const RemoveProviderModal = ({ open, onClose, provider }: RemoveProviderModalProps) => {
  const [selectedReason, setSelectedReason] = useState<ChurnReason | null>(null);
  const [otherReason, setOtherReason] = useState('');
  const radioGroupLabelId = useId();
  const otherReasonInputRef = useRef<HTMLInputElement>(null);

  const showOtherInput = selectedReason === 'unknown';
  const isFormValid = selectedReason !== null && (selectedReason !== 'unknown' || !!otherReason.trim());

  useEffect(() => {
    if (showOtherInput) {
      otherReasonInputRef.current?.focus();
    }
  }, [showOtherInput]);

  useEffect(() => {
    if (open) {
      setSelectedReason(null);
      setOtherReason('');
    }
  }, [open, provider.provider_id]);

  const removeProvider = useRemoveProvider(provider.provider_id, {
    onSuccess: () => {
      toast.custom(() => <Toast variant="success">Provider removed</Toast>);
      onClose();
    },
    onError: () => {
      toast.custom(() => <StandardErrorToast />);
    },
  });

  const handleRemove = () => {
    if (removeProvider.isPending || !isFormValid || !selectedReason) {
      return;
    }

    removeProvider.mutate({
      churn_reason: selectedReason,
      ...(selectedReason === 'unknown' && otherReason.trim()
        ? { specify_other_churned_reason: otherReason.trim() }
        : {}),
    });
  };

  if (!open) return null;

  const providerName = [provider.first_name, provider.last_name].filter(Boolean).join(' ');

  return (
    <Modal onClose={onClose}>
      <ModalTitle>Remove provider?</ModalTitle>
      <ModalBody className="flex flex-col gap-6">
        <Typography>
          <Typography renderedAs="span" weight={600}>
            {providerName && <>{providerName} </>}(NPI: {provider.npi}){' '}
          </Typography>
          will no longer be associated with your organization.
        </Typography>

        <Hairline $marginTop="0" $marginBottom="0" />

        <Typography id={radioGroupLabelId}>
          Tandem will stop associating prescriptions from this NPI with your organization. Please select a reason for
          cancellation:
        </Typography>

        <RadioGroup aria-labelledby={radioGroupLabelId} className="flex flex-col gap-3">
          {reasonOptions.map((option) => {
            const isSelected = selectedReason === option.value;

            return (
              <div key={option.value} className="flex flex-col gap-3">
                <RadioOption htmlFor={option.value}>
                  <RadioButton
                    type="radio"
                    id={option.value}
                    name="removeProviderReason"
                    value={option.value}
                    checked={isSelected}
                    onChange={() => setSelectedReason(option.value)}
                  />
                  <RadioLabel as="span">{option.label}</RadioLabel>
                </RadioOption>

                {isSelected && option.value === 'unknown' && (
                  <div className="ml-10">
                    <Input
                      ref={otherReasonInputRef}
                      label="Please specify the reason for removing this provider"
                      hideLabel
                      placeholder="Please specify"
                      value={otherReason}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => setOtherReason(event.target.value)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </RadioGroup>
      </ModalBody>

      <ModalFooter>
        <Button variant="tertiary" size="sm" onClick={onClose} disabled={removeProvider.isPending}>
          Back
        </Button>
        <Button size="sm" onClick={handleRemove} isLoading={removeProvider.isPending} disabled={!isFormValid}>
          Remove
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default RemoveProviderModal;
